<template>
  <div>
    <t-modal
      v-if="showAddDialog"
      :header="$t('forms.raw_title')"
      xxl
      header-low
      data-testid="dialog"
      @close="() => (showAddDialog = false)"
    >
      <template #dialog-header>
        <TButton
          v-if="!isValidLarge"
          class="ml-auto mr-10"
          danger
          @click="fixHTMLLarge"
        >
          {{ $t("errors.fix_HTML") }}
        </TButton>
        <TButton
          v-if="showBack && isValidLarge"
          class="ml-auto mr-10"
          @click="goBack"
        >
          {{ $t("premade.back") }}
        </TButton>
      </template>
      <template #dialog-body>
        <editor
          :key="validationIndex"
          ref="editorLarge"
          style="height: calc(100vh - 100px); margin-top: 12px"
          :content="content"
          @change-content="changed"
          @fix-content="fixContent"
          @show-back="showBack = true"
          @hide-back="showBack = false"
          @blur="mutationEmptyString = ''"
        >
        </editor>
      </template>
    </t-modal>
    <menu-content-container
      :chip="$t('blocks.html')"
      chip-secondary
      :border-bottom="false"
    >
      <t-note>
        {{ $t("forms.raw_notice") }}
      </t-note>
    </menu-content-container>

    <menu-content-container :border-bottom="false">
      <div
        class="mt-4 rounded-sm border-b border-solid border-gray-500 bg-gray-600"
      >
        <div class="flex items-center justify-end p-0.5">
          <button
            class="grid cursor-pointer rounded-md bg-transparent p-1.5 text-gray-100 transition duration-300 hover:bg-gray-500 hover:text-gray-50"
            @click="() => (showAddDialog = true)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-maximize-2"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" x2="14" y1="3" y2="10" />
              <line x1="3" x2="10" y1="21" y2="14" />
            </svg>
          </button>
        </div>
        <editor
          v-if="!showAddDialog"
          :key="validationIndex"
          ref="editorSmall"
          style="height: 500px"
          :content="content"
          @change-content="changed"
          @fix-content="fixContent"
          @show-back="showBack = true"
          @hide-back="showBack = false"
          @paste="onPaste"
          @blur="mutationEmptyString = ''"
        >
        </editor>
      </div>
      <div class="mt-4">
        <TButton v-if="!isValidSmall" danger @click="fixHTMLSmall">{{
          $t("errors.fix_HTML")
        }}</TButton>

        <TButton v-if="showBack && isValidSmall" @click="goBack">
          {{ $t("premade.back") }}
        </TButton>
      </div>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import Editor from "./elements/editor.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { getCopiedElement } from "@topol/utils";

  import { TModal, TNote, TButton } from "@topol/twig-ui";

  import { computed, ref, onMounted, watch } from "vue";

  const showAddDialog = ref(false);
  const blockIndexes = ref();
  const editorSmall = ref();
  const editorLarge = ref();
  const validationIndex = ref(0);
  const showBack = ref(false);
  const editorContentValue = ref("");
  const valueIsSet = ref(false);
  const mutationEmptyString = ref("");

  const mjmlStore = useMjmlStore();

  const isValidSmall = computed(() => {
    return editorSmall.value ? editorSmall.value.isValid : true;
  });

  const isValidLarge = computed(() => {
    return editorLarge.value ? editorLarge.value.isValid : true;
  });

  const content = computed({
    get() {
      if (mutationEmptyString.value === "content") {
        return "";
      }
      return mjmlStore.getActiveBlockPropString("content");
    },
    set(val: string) {
      updateContent(val);
    },
  });

  function updateContent(val: string, instantSnapshot = false) {
    if (val === content.value) {
      return;
    }
    if (!val && mjmlStore.mjml.langs) {
      // If the value is empty, we set the mutationEmptyString to true
      // to avoid immediate display of the value from the primary mutation.
      mutationEmptyString.value = "content";
    } else {
      mutationEmptyString.value = "";
    }
    // when raw element contains class="mj-column-per-XX" it breaks the renderer, so we need to remove it
    //val = val.replace('mj-column-per-', 'inlined-mj-column-per-')
    if (blockIndexes.value) {
      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: val,
        sectionIndex: blockIndexes.value.sectionIndex,
        columnIndex: blockIndexes.value.columnIndex,
        blockIndex: blockIndexes.value.blockIndex,
        instantSnapshot: instantSnapshot,
      });
    } else {
      mjmlStore.updateBlockProp({
        prop: "content",
        value: val,
        instantSnapshot: instantSnapshot,
      });
    }
  }

  function fixHTMLSmall() {
    if (!editorSmall.value) return;
    editorSmall.value.fixHTML();
    validationIndex.value++;
  }

  function fixHTMLLarge() {
    if (!editorLarge.value) return;
    editorLarge.value.fixHTML();
    validationIndex.value++;
  }

  function goBack() {
    mjmlStore.goBack();
    validationIndex.value++;
  }

  function changed(contentValue: string) {
    content.value = contentValue;
    editorContentValue.value = contentValue;
  }

  function fixContent(contentValue: string) {
    updateContent(contentValue, true);
    editorContentValue.value = contentValue;
  }

  function onPaste(e: ClipboardEvent) {
    if (!e.clipboardData || window.getSelection()?.toString() !== "") {
      return;
    }
    const data = getCopiedElement(e.clipboardData.getData("text"));

    if (data && data.tagName !== "mj-section") {
      mjmlStore.addCopiedBlock({ direction: "bottom", block: data });
    }
  }

  watch(content, (value) => {
    if (!valueIsSet.value) {
      editorContentValue.value = value;
      valueIsSet.value = true;
    }
    if (value !== editorContentValue.value) {
      validationIndex.value++;
      showBack.value = false;
    }
  });

  onMounted(() => {
    blockIndexes.value = JSON.parse(JSON.stringify(mjmlStore.selectedBlock));
  });
</script>
